import { useCallback, useEffect, useMemo, useState } from 'react';
import type { WizardSelectOption } from '../components/ui/WizardSelect';
import {
  formatCategoryAllocationUserLabel,
  listStateAttorneys,
  listSupervisingOfficers,
  type CategoryAllocationUserOption,
} from '../services/categoryAllocationSetup';

type LoadState = 'idle' | 'loading' | 'error';

function toSelectOptions(users: CategoryAllocationUserOption[]): WizardSelectOption[] {
  return users
    .map((user) => ({
      id: user.userId,
      label: user.displayName.trim() || formatCategoryAllocationUserLabel(user),
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

/** CMS-AUQ-8 / CMS-AUQ-9 — supervisor and state attorney dropdown options. */
export function useAssignmentOfficerOptions(active: boolean) {
  const [loadState, setLoadState] = useState<LoadState>('idle');
  const [supervisors, setSupervisors] = useState<CategoryAllocationUserOption[]>([]);
  const [stateAttorneys, setStateAttorneys] = useState<CategoryAllocationUserOption[]>([]);

  const reset = useCallback(() => {
    setLoadState('idle');
    setSupervisors([]);
    setStateAttorneys([]);
  }, []);

  useEffect(() => {
    if (!active) return;

    const controller = new AbortController();
    setLoadState('loading');
    setSupervisors([]);
    setStateAttorneys([]);

    void Promise.all([
      listSupervisingOfficers({ signal: controller.signal }),
      listStateAttorneys({ signal: controller.signal }),
    ]).then(([supervisorRes, stateAttorneyRes]) => {
      if (controller.signal.aborted) return;
      if (!supervisorRes.success || !stateAttorneyRes.success) {
        setLoadState('error');
        return;
      }
      setSupervisors(supervisorRes.data);
      setStateAttorneys(stateAttorneyRes.data);
      setLoadState('idle');
    });

    return () => controller.abort();
  }, [active]);

  const supervisorOptions = useMemo(() => toSelectOptions(supervisors), [supervisors]);
  const stateAttorneyOptions = useMemo(() => toSelectOptions(stateAttorneys), [stateAttorneys]);

  return {
    loadState,
    supervisorOptions,
    stateAttorneyOptions,
    reset,
  };
}
